import { useEffect, type ReactNode } from "react";
import { Command } from "cmdk";
import { ArrowRight, Search, type LucideIcon } from "lucide-react";
import { cn } from "../lib/cn";
import { microcopy, useMicrocopy } from "../lib/microcopy";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./overlays";

// ---------------------------------------------------------------- shortcut hint
export function Shortcut({ keys }: { keys: string[] }) {
  return (
    <span dir="ltr" className="flex items-center gap-1">
      {keys.map((k, i) => (
        <kbd key={i} className="rounded-md border border-hairline bg-white/80 px-1.5 py-0.5 font-mono text-[10px] font-medium text-subtle shadow-[inset_0_-1px_0_rgb(35_37_40/0.08)]">
          {k}
        </kbd>
      ))}
    </span>
  );
}

// ---------------------------------------------------------------- command palette (generic)
export interface PaletteItem {
  id: string;
  label: ReactNode;
  /** Extra words the fuzzy filter should match on (keys, Arabic names, paths). */
  keywords?: string;
  hint?: ReactNode;
  icon?: LucideIcon;
  /** Rendered instead of the icon (e.g. a kind badge). */
  leading?: ReactNode;
  /** Keyboard sequence shown at the end (e.g. `["g", "s"]`). */
  keys?: string[];
  trailing?: ReactNode;
  onSelect: () => void;
}

export interface PaletteGroup {
  id: string;
  heading: ReactNode;
  items: PaletteItem[];
}

export interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Groups are rendered in order; empty groups are skipped. */
  groups: PaletteGroup[];
  query: string;
  onQueryChange: (q: string) => void;
  /** Accessible label of the palette. */
  label: string;
  placeholder: string;
  emptyText: string;
  /** Let cmdk filter items by their text; turn off when the groups already reflect a server-side search. */
  shouldFilter?: boolean;
}

/**
 * Prop-driven command palette on `cmdk`. Apps supply the groups (navigation, actions, recent, search hits) and own routing and
 * data fetching; the palette owns the dialog, the input, keyboard handling and the look.
 */
export function CommandPalette({ open, onOpenChange, groups, query, onQueryChange, label, placeholder, emptyText, shouldFilter = true }: CommandPaletteProps) {
  useEffect(() => {
    if (open) onQueryChange("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const pick = (item: PaletteItem) => {
    onOpenChange(false);
    item.onSelect();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg" className="top-[18%] translate-y-0 gap-0 overflow-hidden p-0 sm:top-[14%]" aria-describedby={undefined} hideClose>
        <Command label={label} className="flex max-h-[min(70dvh,36rem)] flex-col" shouldFilter={shouldFilter} loop>
          <div className="flex items-center gap-3 border-b border-hairline px-5 has-[input:focus-visible]:shadow-[inset_0_-2px_0_var(--color-ring)]">
            <Search className="size-5 shrink-0 text-aegold-700" />
            <Command.Input value={query} onValueChange={onQueryChange} placeholder={placeholder} className="h-14 w-full bg-transparent text-base text-strong outline-none placeholder:text-sand-500 focus-visible:ring-0 focus-visible:ring-offset-0" autoFocus />
            <DialogClose aria-label={microcopy("close")} className="shrink-0 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-ring">
              <kbd className="block rounded-md border border-hairline bg-white/80 px-1.5 py-0.5 text-[10px] font-medium text-subtle shadow-[inset_0_-1px_0_rgb(35_37_40/0.08)] hover:bg-white">esc</kbd>
            </DialogClose>
          </div>
          <Command.List className="scrollbar-thin overflow-y-auto p-2">
            <Command.Empty className="px-3 py-8 text-center text-sm text-muted-foreground">{emptyText}</Command.Empty>
            {groups
              .filter((g) => g.items.length > 0)
              .map((g) => (
                <Command.Group key={g.id} heading={g.heading} className={groupClass}>
                  {g.items.map((item) => (
                    <Command.Item key={item.id} value={`${g.id} ${item.id} ${typeof item.label === "string" ? item.label : ""} ${item.keywords ?? ""}`} onSelect={() => pick(item)} className={itemClass}>
                      {item.leading ?? (item.icon ? <item.icon className="size-4 text-aegold-700" /> : <ArrowRight className="size-4 text-sand-500" />)}
                      <span className="flex-1 truncate">{item.label}</span>
                      {item.hint ? <span className="truncate text-xs text-muted-foreground">{item.hint}</span> : null}
                      {item.trailing}
                      {item.keys ? <Shortcut keys={item.keys} /> : null}
                    </Command.Item>
                  ))}
                </Command.Group>
              ))}
          </Command.List>
          <PaletteFooter />
        </Command>
      </DialogContent>
    </Dialog>
  );
}

function PaletteFooter() {
  const m = useMicrocopy();
  return (
    <div className="flex items-center gap-4 border-t border-hairline bg-white/40 px-5 py-2.5 text-[11px] text-subtle" aria-hidden>
      <span className="flex items-center gap-1.5"><Shortcut keys={["↑", "↓"]} /> {m("paletteNavigate")}</span>
      <span className="flex items-center gap-1.5"><Shortcut keys={["↵"]} /> {m("paletteOpen")}</span>
      <span className="flex items-center gap-1.5"><Shortcut keys={["esc"]} /> {m("paletteDismiss")}</span>
    </div>
  );
}

const groupClass = "[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:pb-1.5 [&_[cmdk-group-heading]]:pt-3 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-subtle";
const itemClass = cn(
  "flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-foreground outline-none transition-colors",
  "data-[selected=true]:bg-aegold-50/90 data-[selected=true]:text-aegold-900 data-[selected=true]:shadow-[inset_0_0_0_1px_rgb(182_138_53/0.22)] aria-selected:bg-aegold-50/90",
);

// ---------------------------------------------------------------- shortcuts dialog
export interface ShortcutRow {
  keys: string[];
  label: ReactNode;
}

export function ShortcutsDialog({ open, onOpenChange, title, description, rows }: { open: boolean; onOpenChange: (o: boolean) => void; title: ReactNode; description?: ReactNode; rows: ShortcutRow[] }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description ? <DialogDescription>{description}</DialogDescription> : null}
        </DialogHeader>
        <ul className="divide-y divide-hairline text-sm">
          {rows.map((r, i) => (
            <li key={i} className="flex items-center justify-between gap-4 py-2">
              <span>{r.label}</span>
              <Shortcut keys={r.keys} />
            </li>
          ))}
        </ul>
      </DialogContent>
    </Dialog>
  );
}
